/**
 * MOTOR DE ILUMINACIÓN Y AUTOMATIZACIÓN — cuánta luz hace falta y con qué se controla.
 * =============================================================================
 *
 * Quinto rubro. Es el que más se vende «a ojo» en el mercado y el que más fácil se
 * defiende con un número, porque el cliente **sí nota** la diferencia entre una cocina a
 * 150 lux y una a 400.
 *
 * ---------------------------------------------------------------------------
 * LO PRIMERO QUE HAY QUE ENTENDER, PORQUE ORDENA TODO EL ARCHIVO
 *
 * **En México hay norma de iluminación para centros de trabajo, y NO la hay para
 * vivienda.** No es un descuido de este archivo: es un hecho verificado.
 *
 * La `NOM-025-STPS-2008` declara en su punto 2 que *«rige en todo el territorio nacional y
 * aplica en todos los centros de trabajo»*. Una sala o una recámara no son un centro de
 * trabajo, así que sus 300 lux de oficina no son exigibles ahí. Y la norma europea que
 * suele citarse, `EN 12464-1`, se titula literalmente *indoor work places*: tampoco cubre
 * vivienda.
 *
 * Consecuencia práctica, y es comercial además de técnica:
 *
 *   · Si Juan ilumina **la oficina de un despacho de arquitectura, un local o un taller**,
 *     hay un número que la ley exige y que una inspección puede medir con luxómetro. Eso
 *     es un argumento de venta duro y una responsabilidad real.
 *   · Si ilumina **una sala**, hay una recomendación de confort que se negocia con el
 *     cliente. Prometer «cumplimiento de norma» ahí sería vender algo que no existe.
 *
 * Este archivo mantiene las dos tablas separadas y **etiquetadas distinto** justamente
 * para que esa frase no se diga por error.
 * ============================================================================= */

import { area } from './geometria.js'
import { resolver, renglon } from './catalogo-tecnico.js'

export { area }

/* ============================================================================
   1. NIVELES EXIGIBLES — centros de trabajo
   ========================================================================== */

/**
 * Tabla 1 de la NOM-025-STPS-2008, «Niveles de Iluminación».
 *
 * `FUENTE:` **texto primario**, transcrita del DOF del 30/12/2008
 * (`dof.gob.mx/nota_detalle.php?codigo=5076393`), descargada con `curl --ssl-no-revoke`.
 *
 * Ojo con el host: el certificado del DOF **sólo cubre `dof.gob.mx`**, sin `www`. Con
 * `www` la descarga falla por desajuste de certificado. Ya costó una vuelta.
 *
 * `lux` es el **mínimo exigible**, no un objetivo de diseño: quedarse justo en el número
 * significa que cualquier lámpara sucia o envejecida pone la instalación fuera de norma.
 * Por eso el cálculo aplica factor de mantenimiento — ver `FACTOR_MANTENIMIENTO`.
 */
export const NOM025 = [
  { lux: 20, tarea: 'Distinguir el área de tránsito, caminar, vigilancia, movimiento de vehículos',
    area: 'Exteriores generales: patios y estacionamientos' },
  { lux: 50, tarea: 'Lo mismo, pero en interiores',
    area: 'Almacenes de poco movimiento, pasillos, escaleras, estacionamientos cubiertos, iluminación de emergencia' },
  { lux: 100, tarea: 'Permanencia sin tarea visual',
    area: 'Áreas de circulación y pasillos; salas de espera; salas de descanso; cuartos de almacén; cuartos de calderas' },
  { lux: 200, tarea: 'Requerimiento visual simple: inspección visual, recuento de piezas, trabajo en banco y máquina',
    area: 'Servicios al personal: almacenaje rudo, recepción y despacho, casetas de vigilancia' },
  { lux: 300, tarea: 'Distinción moderada de detalles: ensamble simple, inspección simple, empaque y trabajos de oficina',
    area: 'Talleres: áreas de empaque y ensamble, aulas y oficinas' },
  { lux: 500, tarea: 'Distinción clara de detalles: acabados delicados, captura y procesamiento de información, instrumentos de laboratorio',
    area: 'Talleres de precisión: salas de cómputo, áreas de dibujo, laboratorios' },
  { lux: 750, tarea: 'Distinción fina de detalles: maquinado de precisión, piezas pequeñas',
    area: 'Talleres de alta precisión: pintura y acabado de superficies, control de calidad' },
  { lux: 1000, tarea: 'Alta exactitud: ensamble e inspección de piezas pequeñas y complejas, pulidos finos',
    area: 'Proceso: piezas complejas y acabados con pulidos finos' },
  { lux: 2000, tarea: 'Alto grado de especialización: bajo contraste y tamaño muy pequeño por periodos prolongados',
    area: 'Proceso de gran exactitud' }
]

/** El campo de aplicación, literal, porque es lo que decide si el número es exigible. */
export const ALCANCE_NOM025 =
  'La NOM-025-STPS-2008 «rige en todo el territorio nacional y aplica en todos los centros ' +
  'de trabajo». Una vivienda NO es un centro de trabajo: en casa estos números son ' +
  'referencia, no obligación.'

/* ============================================================================
   2. NIVELES RECOMENDADOS — vivienda
   ========================================================================== */

/**
 * ⚠️ `CONFIRMAR:` **estos valores no son norma y no se leyeron de un texto primario.**
 *
 * No existe norma vinculante de iluminación para vivienda, ni en México ni en Europa: la
 * NOM-025 aplica a centros de trabajo y la EN 12464-1 se titula *indoor work places*. Lo
 * que existe son recomendaciones de la práctica —CIBSE, IES— reproducidas en guías de la
 * industria, y de ahí salen estos rangos. Se intentó llegar al texto de CIBSE y de la
 * EN 12464-1 y **los dos son de pago**.
 *
 * Se cargan igual, y marcados, porque sin un número de partida el operador no puede
 * proponer nada. Pero **se presentan como rango y como recomendación**, no como
 * cumplimiento, y la pantalla lo dice con esas palabras.
 *
 * `tarea` es el nivel más alto que hace falta en ese espacio, y casi siempre está en una
 * superficie concreta —la barra de la cocina, el espejo del baño— no en todo el cuarto.
 * Ésa es la diferencia entre iluminar un cuarto e iluminar lo que ahí se hace, y es lo que
 * separa una instalación buena de una que sólo es brillante.
 */
export const ESPACIOS = [
  { id: 'sala', nombre: 'Sala', general: 150, tarea: 300,
    donde: 'La lectura pide más que la plática. Un nivel general suave con luz puntual donde se lee.' },
  { id: 'comedor', nombre: 'Comedor', general: 150, tarea: 300,
    donde: 'Sobre la mesa. El resto del cuarto puede ir más bajo sin que se note.' },
  { id: 'cocina', nombre: 'Cocina', general: 300, tarea: 500,
    donde: 'La barra y la estufa. Es donde se corta y donde hay fuego: es el espacio que más luz pide de la casa.' },
  { id: 'recamara', nombre: 'Recámara', general: 100, tarea: 300,
    donde: 'La cabecera para leer y el clóset para elegir ropa. El resto quiere estar bajo.' },
  { id: 'bano', nombre: 'Baño', general: 150, tarea: 400,
    donde: 'El espejo, y de frente, no desde arriba: la luz cenital hace sombras en la cara.' },
  { id: 'estudio', nombre: 'Estudio u oficina en casa', general: 300, tarea: 500,
    donde: 'El escritorio. Si ahí trabaja alguien contratado, ya es centro de trabajo y aplica la NOM-025.' },
  { id: 'pasillo', nombre: 'Pasillos y escaleras', general: 100, tarea: 100,
    donde: 'Uniforme, sin zonas oscuras. En escaleras la sombra es un riesgo, no una decisión estética.' },
  { id: 'exterior', nombre: 'Jardín y accesos', general: 20, tarea: 50,
    donde: 'Lo suficiente para caminar seguro. Más luz afuera deslumbra y no deja ver.' },
  { id: 'cochera', nombre: 'Cochera', general: 75, tarea: 150,
    donde: 'Maniobrar el coche. Si además se usa como taller, sube al nivel de la tarea.' }
]

export const espacioPorId = id => ESPACIOS.find(e => e.id === id) || ESPACIOS[0]

/* ============================================================================
   3. EL CÁLCULO
   ========================================================================== */

/**
 * Factor de mantenimiento: cuánta luz se pierde por suciedad y envejecimiento.
 *
 * `CONFIRMAR:` 0.8 es el valor de práctica habitual para interiores limpios con luminaria
 * cerrada. No se leyó de una fuente primaria en esta sesión.
 *
 * **Por qué existe y no se puede omitir:** una luminaria entrega su flujo nominal el día
 * que se instala y menos cada mes que pasa. Diseñar justo al número exigido garantiza
 * estar por debajo en un año. Es el mismo razonamiento que la holgura de obra: se declara,
 * no se esconde dentro del resultado.
 */
export const FACTOR_MANTENIMIENTO = { valor: 0.8, sinVerificar: true }

/**
 * Índice del local (Room Cavity Ratio). Fórmula estándar de la técnica de iluminación:
 *
 *     RCR = 5 · h · (L + A) / (L · A)
 *
 * donde `h` es la altura entre la luminaria y el plano de trabajo. Es geometría, no tabla.
 *
 * **Para qué sirve:** dice qué tan «alto y angosto» es un cuarto. Un pasillo alto y
 * estrecho desperdicia luz en los muros; un cuarto bajo y ancho la aprovecha casi toda. De
 * aquí sale el coeficiente de utilización en las tablas fotométricas del fabricante.
 */
export function indiceLocal (largo, ancho, alturaMontaje) {
  if (!largo || !ancho || !alturaMontaje) return 0
  return (5 * alturaMontaje * (largo + ancho)) / (largo * ancho)
}

/**
 * COEFICIENTE DE UTILIZACIÓN — la parte del flujo que llega al plano de trabajo.
 *
 * ⚠️ **Aquí este archivo se detiene a propósito, y conviene explicar por qué.**
 *
 * El coeficiente real sale de la **tabla fotométrica de la luminaria concreta**, cruzando
 * el índice del local con las reflectancias de techo, muros y piso. Cada fabricante
 * publica la suya y no hay una tabla universal.
 *
 * Se podría inventar una tabla plausible. **No se hizo**, por la misma razón por la que la
 * bomba se selecciona de la curva de Pedrollo y las distancias de cámara salen de la ficha
 * de Hikvision: un número inventado que nadie vuelve a mirar es indistinguible de un dato
 * bueno seis meses después, y éste gobierna cuántas luminarias se cotizan.
 *
 * Lo que sí se hace es dar un **valor de trabajo declarado y editable**, con el índice del
 * local calculado al lado para que quien tenga la tabla del fabricante pueda sustituirlo
 * en un renglón. Los tres valores son los tramos gruesos que reconoce cualquiera que haya
 * visto una tabla fotométrica; la precisión la pone la tabla real.
 */
/**
 * ⚠️ **CORREGIDO EL 2026-08-15, Y ERA EXACTAMENTE EL ERROR QUE ESTE COMENTARIO DECÍA NO
 * COMETER.** La primera versión mapeaba el índice del local a tres tramos de utilización
 * —0.60, 0.45 y 0.30— con umbrales que yo puse. Eso es inventar la tabla fotométrica con
 * otro nombre.
 *
 * Y el efecto no era teórico. Una cocina normal de 4 × 3 m da índice 6.7, que caía en el
 * tramo «desfavorable» de 0.30, y el motor pedía **19 luminarias empotradas para 12 m²**.
 * Una cocina real lleva ocho o diez. El módulo sobre-especificaba al doble, que en una
 * cotización es perder la venta por caro y en obra es un techo lleno de agujeros.
 *
 * Lo detectó una prueba de cordura: «una cocina normal no puede pedir tantas luminarias».
 * Mismo tipo de prueba que destapó el modelo de Wi-Fi — no comprueba una fórmula, comprueba
 * que el resultado sea creíble.
 *
 * **El mapeo se eliminó.** Queda un solo valor de trabajo, declarado, editable, y el índice
 * del local se calcula y se muestra **al lado** para que quien tenga la tabla fotométrica
 * de su luminaria lo sustituya en un renglón. Un número honesto y ajustable vale más que
 * tres números inventados que parecen precisión.
 */
export const UTILIZACION = {
  sinVerificar: true,
  fuente: 'valor de trabajo. El real sale de la tabla fotométrica de la luminaria elegida, ' +
          'cruzando el índice del local con las reflectancias de techo, muros y piso',
  /**
   * Valor único de partida para luminaria empotrada en vivienda con acabados claros.
   * `CONFIRMAR:` no se leyó de una tabla fotométrica; se eligió porque produce cantidades
   * que coinciden con lo que se instala en la práctica, y ése es un criterio débil que
   * conviene sustituir en cuanto haya una luminaria elegida.
   */
  valor: 0.50
}

/**
 * Devuelve el coeficiente a usar y el índice del local para poder corregirlo.
 *
 * NO deduce el coeficiente del índice: ésa es justo la invención que se quitó. El índice
 * viaja como dato para que el operador —o Juan— entre a la tabla del fabricante.
 */
export function utilizacionPara (rcr, valor = UTILIZACION.valor) {
  return {
    valor,
    rcr,
    /** Lectura del índice en palabras. Es descripción, no cálculo: no cambia el número. */
    forma: !rcr ? 'sin medir'
      : rcr <= 2.5 ? 'espacio amplio y de techo bajo'
      : rcr <= 6 ? 'proporciones normales de vivienda'
      : 'espacio angosto o de techo alto',
    porque: 'El coeficiente es un valor de trabajo. El exacto sale de la tabla fotométrica ' +
            `de la luminaria que se elija, entrando con el índice del local (${rcr.toFixed(1)}).`
  }
}

/**
 * MÉTODO DE LÚMENES — cuántas luminarias hacen falta.
 *
 *     n = (E · A) / (Φ · CU · FM)
 *
 * donde `E` es el nivel buscado en lux, `A` el área en m², `Φ` el flujo de una luminaria en
 * lúmenes, `CU` el coeficiente de utilización y `FM` el factor de mantenimiento.
 *
 * El único dato que hay que ir a buscar es `Φ`, y **está impreso en la caja de la
 * lámpara**. Es exactamente el tipo de dato que el Principio 7 pide: observable por quien
 * no es técnico.
 *
 * Se redondea **hacia arriba**: media luminaria no existe, y quedarse abajo del nivel es
 * el defecto que el cliente sí nota.
 */
export function cuantasLuminarias ({ lux, areaM2, lumenes, cu, fm = FACTOR_MANTENIMIENTO.valor }) {
  if (!lumenes || !cu || !fm) return { cantidad: 0, exacto: 0 }
  const exacto = (lux * areaM2) / (lumenes * cu * fm)
  const cantidad = Math.max(1, Math.ceil(exacto))
  return {
    cantidad, exacto,
    /** Lo que de verdad va a dar con las luminarias enteras que se van a instalar. */
    luxReales: (cantidad * lumenes * cu * fm) / areaM2,
    porque: `${lux} lux sobre ${areaM2.toFixed(1)} m² con luminarias de ${lumenes} lm, ` +
            `${Math.round(cu * 100)}% de utilización y ${Math.round(fm * 100)}% de mantenimiento.`
  }
}

/* ============================================================================
   4. CONTROL Y AUTOMATIZACIÓN
   ========================================================================== */

/**
 * Cómo se controla cada espacio. Es la mitad del rubro que de verdad se vende.
 *
 * Los tres primeros no son «niveles de lujo»: resuelven problemas distintos. Vender
 * atenuación donde hacía falta un apagador de escalera es caro y no arregla nada.
 */
export const CONTROL = [
  {
    id: 'sencillo', nombre: 'Apagador sencillo', dispositivos: 0,
    que: 'Enciende y apaga desde un punto.',
    para: 'Cuartos con una sola entrada donde no hace falta más.'
  },
  {
    id: 'escalera', nombre: 'Conmutado de escalera', dispositivos: 0,
    que: 'Enciende en un punto y apaga en otro.',
    para: 'Pasillos, escaleras y cuartos con dos entradas. Es cableado, no automatización.'
  },
  {
    id: 'inteligente', nombre: 'Control inteligente', dispositivos: 1,
    que: 'Se enciende desde el celular, por voz o por horario, y sigue funcionando con el apagador de pared.',
    para: 'Donde el cliente quiere escenas, horarios o control remoto.'
  },
  {
    id: 'atenuado', nombre: 'Atenuación inteligente', dispositivos: 1,
    que: 'Todo lo anterior más regular la intensidad.',
    para: 'Sala, comedor y recámara, donde la misma luz sirve para leer y para ver una película.'
  },
  {
    id: 'presencia', nombre: 'Encendido por presencia', dispositivos: 1,
    que: 'Se enciende solo al entrar y se apaga solo al salir.',
    para: 'Pasillos, clósets, baños de visita y cocheras. Es lo que más ahorra y lo que más se agradece.'
  }
]

export const controlPorId = id => CONTROL.find(c => c.id === id) || CONTROL[0]

/**
 * ⚠️ REGLA QUE UN VENDEDOR NO TÉCNICO NO PUEDE SABER, Y QUE ARRUINA INSTALACIONES.
 *
 * **No toda lámpara se puede atenuar.** Un módulo de atenuación sobre una lámpara LED que
 * no es atenuable produce parpadeo, zumbido y una vida útil corta — y el cliente lo reporta
 * como falla de la automatización, no de la lámpara.
 *
 * Además la mayoría de los módulos de atenuación exigen una **carga mínima**: con una sola
 * lámpara LED de pocos watts, el módulo no ve suficiente carga y la deja parpadeando.
 *
 * `sinVerificar:` el umbral concreto depende del módulo y viene en su ficha. Se declara
 * como advertencia, no como cálculo, porque advertir sin número sigue siendo útil y
 * poner un número inventado no lo es.
 */
export const AVISO_ATENUACION = {
  sinVerificar: true,
  texto: 'La atenuación exige lámparas marcadas como atenuables y una carga mínima que ' +
         'depende del módulo. Con una sola lámpara LED chica es muy probable que parpadee. ' +
         'Verifícalo en la ficha del módulo antes de prometerlo.'
}

/* ============================================================================
   5. LA CADENA COMPLETA
   ========================================================================== */

/**
 * Calcula un espacio: cuánta luz pide, cuántas luminarias son, y con qué se controla.
 *
 * `esCentroDeTrabajo` es la bandera que decide si el número es **exigible o recomendado**,
 * y por eso se pregunta en vez de deducirse: un estudio donde el dueño lee no es lo mismo
 * que un estudio donde trabaja un empleado contratado.
 */
export function calcularEspacio ({
  espacioId, largo, ancho, alturaMontaje = 2.3, lumenes = 800,
  control = 'sencillo', esCentroDeTrabajo = false, luxObjetivo = null,
  fm = FACTOR_MANTENIMIENTO.valor, cu = UTILIZACION.valor
}) {
  const esp = espacioPorId(espacioId)
  const areaM2 = largo * ancho
  const rcr = indiceLocal(largo, ancho, alturaMontaje)
  const util = utilizacionPara(rcr, cu)

  const lux = luxObjetivo || esp.general
  const luminarias = cuantasLuminarias({ lux, areaM2, lumenes, cu: util.valor, fm })

  /* Si el espacio es centro de trabajo, se busca el renglón de la NOM que le toca. No se
     adivina cuál: se toma el mínimo exigible que sea igual o mayor al nivel de diseño, y
     se reporta para que alguien lo confirme contra la tarea real. */
  const exigible = esCentroDeTrabajo
    ? NOM025.filter(n => n.lux <= lux).pop() || NOM025[0]
    : null

  return {
    espacio: esp, areaM2, rcr,
    utilizacion: util,
    lux, luminarias,
    control: controlPorId(control),
    /** Exigible o recomendado. Es la distinción que ordena el módulo entero. */
    regimen: esCentroDeTrabajo ? 'norma' : 'recomendacion',
    exigible,
    porqueRegimen: esCentroDeTrabajo
      ? `Es centro de trabajo, así que la NOM-025-STPS-2008 aplica y el nivel es exigible. ` +
        `Una inspección lo puede medir con luxómetro.`
      : 'Es vivienda. No hay norma que lo exija: el nivel es una recomendación de confort ' +
        'que se acuerda con el cliente.',
    avisoAtenuacion: control === 'atenuado' ? AVISO_ATENUACION.texto : null
  }
}

/**
 * La casa completa: la suma de sus espacios.
 */
export function calcular ({ espacios = [], insumos = [], fm = FACTOR_MANTENIMIENTO.valor, cu = UTILIZACION.valor }) {
  const detalle = espacios.map(e => calcularEspacio({ ...e, fm, cu }))

  const totalLuminarias = detalle.reduce((s, d) => s + d.luminarias.cantidad, 0)
  const totalArea = detalle.reduce((s, d) => s + d.areaM2, 0)
  const conNorma = detalle.filter(d => d.regimen === 'norma')

  /* Los módulos de control se cuentan por ESPACIO, no por luminaria: un módulo maneja
     todo el circuito del cuarto. Contarlos por lámpara es el error que multiplica el
     presupuesto por cinco y hace perder la venta. */
  const porControl = {}
  for (const d of detalle) {
    if (!d.control.dispositivos) continue
    porControl[d.control.id] = (porControl[d.control.id] || 0) + d.control.dispositivos
  }

  return {
    detalle,
    totalArea, totalLuminarias,
    espaciosConNorma: conNorma.length,
    porControl,
    materiales: materiales({ detalle, porControl, insumos })
  }
}

export function materiales ({ detalle, porControl, insumos = [] }) {
  const lista = []

  const totalLuminarias = detalle.reduce((s, d) => s + d.luminarias.cantidad, 0)
  if (totalLuminarias) {
    lista.push(renglon({
      insumo: resolver(insumos, { clase: 'luminaria' }),
      cantidad: totalLuminarias, unidad: 'pieza',
      de: `Método de lúmenes espacio por espacio: ` +
          detalle.map(d => `${d.espacio.nombre} ${d.luminarias.cantidad}`).join(', ')
    }))
  }

  for (const [id, cantidad] of Object.entries(porControl)) {
    const c = controlPorId(id)
    lista.push(renglon({
      insumo: resolver(insumos, { clase: 'control-luz', control: id }),
      cantidad, unidad: 'pieza',
      de: `Uno por espacio con ${c.nombre.toLowerCase()}, no uno por lámpara: ` +
          'el módulo maneja todo el circuito del cuarto'
    }))
  }

  return lista
}
